
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, Printer } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import WorksheetTypeSelector from "@/components/worksheets/WorksheetTypeSelector";
import WorksheetViewer from "@/components/worksheets/WorksheetViewer";
import PrintableWorksheet from "@/components/worksheets/PrintableWorksheet";
import { generateWorksheet } from "@/services/worksheetGeneratorService";
import { Worksheet } from "@/types/worksheet";
import Seo from "@/components/seo/Seo";

const Worksheets = () => {
  const { grade } = useParams();
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState<string>("");
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPrintable, setShowPrintable] = useState(false);

  const handleTypeSelect = async (type: string, subject: string, topic: string) => {
    setSelectedType(type);
    setIsGenerating(true);
    
    try {
      const generatedWorksheet = await generateWorksheet({
        type: type as 'puzzle' | 'matching' | 'sorting',
        grade: grade || "1",
        subject,
        difficulty: 'easy'
      });
      
      const newWorksheet: Worksheet = {
        id: crypto.randomUUID(),
        title: `${subject} ${type.charAt(0).toUpperCase() + type.slice(1)} Activity`,
        type: {
          id: type,
          name: `${type.charAt(0).toUpperCase() + type.slice(1)} Games`,
          description: `Fun ${type} activities`,
          icon: type,
          category: type as 'puzzle' | 'matching' | 'sorting',
          minGrade: 1,
          maxGrade: 3
        },
        grade: grade || "1",
        subject,
        content: generatedWorksheet.content,
        difficulty: 'easy',
        createdAt: new Date().toISOString()
      };
      
      setWorksheet(newWorksheet);
      toast({
        title: "Worksheet Generated!",
        description: "Your worksheet is ready to use.",
      });
    } catch (error) {
      console.error('Error generating worksheet:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate worksheet. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    setShowPrintable(true);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownload = () => {
    if (!worksheet) return;
    
    const dataStr = JSON.stringify(worksheet, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${worksheet.title.replace(/\s+/g, '_')}_Grade_${grade}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleBack = () => {
    setWorksheet(null);
    setSelectedType("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo
        title={grade ? `Grade ${grade} Worksheets` : 'Worksheets for Grades 1–9'}
        description="Generate printable worksheets — puzzles, matching, and sorting activities — for any subject and grade level."
        canonicalPath={grade ? `/worksheets/${grade}` : '/worksheets'}
      />
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-purple-600" />
                <h1 className="text-2xl font-bold text-gray-800">
                  Worksheets - Grade {grade}
                </h1>
              </div>
            </div>
            
            {worksheet && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePrint}
                  className="flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
              </div>
            )}
          </div>

          {!worksheet ? (
            <WorksheetTypeSelector
              grade={grade || "1"}
              onTypeSelect={handleTypeSelect}
              isGenerating={isGenerating}
            />
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  onClick={handleBack}
                >
                  Generate New Worksheet
                </Button>
              </div>
              
              <WorksheetViewer worksheet={worksheet} onBack={handleBack} />
            </div>
          )}

          {showPrintable && worksheet && (
            <div className="fixed inset-0 bg-white z-50 overflow-auto print:relative print:inset-auto print:z-auto">
              <div className="no-print p-4 border-b">
                <Button
                  onClick={() => setShowPrintable(false)}
                  variant="outline"
                >
                  Close Print View
                </Button>
              </div>
              <PrintableWorksheet worksheet={worksheet} />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Worksheets;
